import React, { useContext, useEffect, useRef, useState } from "react";
import { SmartContext } from '../../../library/Core/SmartContext';
import { Dropdown, Modal, Tooltip } from "bootstrap";
import useInternalAdminReports from "../reports/useInternalAdminReports";
import { useNavigate } from "react-router-dom";
import ExportToExcelCommon from "../../../library/SimpleControls/ExportToExcelCommon";
import SchoolStatisticsService from "./SchoolStatisticsService";
import { axiosClient } from "../../../library/Service/axiosClient";
import Swal from "sweetalert2";

const BATCH_SIZE = 10;
const DEFAULT_SORT_COLUMN = 'category';
const DEFAULT_SORT_DIRECTION = 'asc';

const SchoolStatisticGrid = (data : any) => {
    const { state, dispatch } = useContext(SmartContext);
    const navigate = useNavigate();     
    const { getFormattedDate } = useInternalAdminReports();
    const [sortedStudents, setSortedStudents] = useState<any[]>([]);
    const [expandedCategories, setExpandedCategories] = useState<Record<string, boolean>>({});
    const dropdownRef = useRef(null);
    const [sortColumn, setSortColumn] = useState(DEFAULT_SORT_COLUMN);
    const [sortDirection, setSortDirection] = useState(DEFAULT_SORT_DIRECTION);

    const allRows = data?.fieldList;
    const [selectedQuarter, setSelectedQuarter] = useState<string>('ALL');

    useEffect(() => {
        if (dropdownRef.current) {
            new (Dropdown as any)(dropdownRef.current);
        }
    }, []);

    useEffect(() => {
        const sorted = [...allRows].sort(compareFunction);
        setSortedStudents(sorted);
    }, [data?.fieldList, sortColumn, sortDirection]);

    useEffect(() => {
        const els: Element[] = Array.from(document.querySelectorAll('[data-bs-toggle="tooltip"].smart-tooltip'));
        const tooltipList = els.map((el) => {
            const rawTitle = el.getAttribute('data-bs-title') ?? el.getAttribute('title');
            if (!rawTitle || rawTitle.trim() === '' || rawTitle === 'null') return null;
            try {
                return new Tooltip(el as HTMLElement, {
                    title: rawTitle,
                    trigger: 'hover',
                    container: 'body',
                    delay: { show: 0, hide: 100 }
                });
            } catch (e) {
                console.error('Tooltip init error', e);
                return null;
            }
        }).filter(Boolean) as Tooltip[];

        return () => {
            tooltipList.forEach((tooltip) => {
                if (tooltip && typeof tooltip.dispose === 'function') {
                    tooltip.dispose();
                }
            });
        };
    }, [sortedStudents]);

    // Note: grouping & expand/collapse implemented below; infinite-scroll removed for grouped view

    const compareFunction = (a: any, b: any) => {
        const aValue = a[sortColumn];
        const bValue = b[sortColumn];
        const isStringColumn = typeof aValue === 'string';

        if (isStringColumn) {
            return sortDirection === 'asc'
                ? aValue?.localeCompare(bValue, undefined, { sensitivity: 'accent' })
                : bValue?.localeCompare(aValue, undefined, { sensitivity: 'accent' });
        } else {
            return sortDirection === 'asc' ? (aValue > bValue ? 1 : -1) : aValue < bValue ? 1 : -1;
        }
    };

    const toggleCategory = (category: string) => {
        setExpandedCategories((prev) => ({ ...prev, [category]: !prev[category] }));
    };

    // Group rows by category and build index map for serial numbers
    const groupedRows: Record<string, any[]> = (sortedStudents || []).reduce((acc: Record<string, any[]>, row: any) => {
        const cat = row?.category || 'Uncategorized';
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(row);
        return acc;
    }, {});

    const rowIndexMap: Record<string, number> = Object.fromEntries((sortedStudents || []).map((r: any, i: number) => [r.trackerFieldMasterId, i + 1]));

    // Expand all categories by default whenever sortedStudents changes
    useEffect(() => {
        const cats = Object.keys((sortedStudents || []).reduce((acc: Record<string, any[]>, row: any) => {
            const cat = row?.category || 'Uncategorized';
            if (!acc[cat]) acc[cat] = [];
            acc[cat].push(row);
            return acc;
        }, {}));
        if (cats.length) {
            const init: Record<string, boolean> = {};
            cats.forEach((c) => (init[c] = true));
            setExpandedCategories(init);
        }
    }, [sortedStudents]);

    const handleOpenModal = async () => {
        dispatch({ type: 'SET_MODAL_DATA', payload: {data:{selectedQuarter}} });
        dispatch({ type: 'TOGGLE_MODAL_VISIBILITY', payload: { modalName: 'showSchoolTrackerFileLogs'} });
    };
    
   const loadSchoolStatistics = async (quarterCode: string) => {
        try {
            const response = await SchoolStatisticsService.getSchoolFieldList(
                state?.data?.schoolCount?.[0]?.schoolId,
                quarterCode
            );

            const responseCount = await SchoolStatisticsService.getSchoolCount(
                state?.data?.schoolCount?.[0]?.schoolId,
                quarterCode
            );
            
            dispatch({type: 'REFRESH_DATA',payload: { data: { ...state.data, schoolCount: responseCount?.data,fieldList: response.data} },}); //FOr cards Count update
            console.log("response", response.data);
        } catch (error) {
            console.error("Error loading statistics:", error);
        }
    };

    const downloadExportToPPT = async (institteId: any) => {
        const loader = Swal.fire({
            title: 'Preparing your presentation…',
            html: 'We’re gathering and organizing your content..!',

            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading(); // Show loading spinner
            },
        });
        try {
            const apiResponse = await SchoolStatisticsService.exportToPPT(institteId);
            console.log("apiResponse: ", apiResponse)
            const payload = apiResponse.data;
            const apiUrl = `${process.env.REACT_APP_APP_BASE_URL}/hope/baseline-reports/snapshot`;
            Swal.close();
            const loader = Swal.fire({
                title: 'Generating your slides...',
                html: 'We’re putting the finishing touches on your slides..!',

                allowOutsideClick: false,
                didOpen: () => {
                    Swal.showLoading(); // Show loading spinner
                },
            });

            const response = await axiosClient().post(apiUrl, payload, {
                responseType: 'blob' // Important: specify blob response type
            });
            
            // Create blob from response
            const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
            
            // Extract filename from Content-Disposition header or use default
            const contentDisposition = response.headers['content-disposition'];
            let filename = 'SCHOOL_snapshot_report.pptx';
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
                if (filenameMatch) {
                    filename = filenameMatch[1].replace(/"/g, '');
                }
            }
            
            // Create temporary download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            Swal.close();
            link.click();
            
            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            
        } catch (error) {
            console.error("Error downloading PPT:", error);
        }
    }

return (
        <> 
        <div className="p-4 p-md-5">

        
            <div className="table-responsive attendee_dtl">
                <div className="row g-3 align-items-center mb-3">
                    <div className="col font-500">Total {allRows.length} rows</div>
                    <div className="col-auto">
                        <div className="row g-3 align-items-center">
                            <div className="col-auto">
                                {state?.data?.quarters?.length > 0 && (
                                    <select
                                        className="form-select onboarding m-0"
                                        value={selectedQuarter}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setSelectedQuarter(value);
                                            loadSchoolStatistics(value);
                                        }}
                                    >
                                        {state.data.quarters.map((quarter: any) => (
                                            <option key={quarter.cODE} value={quarter.cODE}>
                                                {quarter.label}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </div>
                            <div className="col-auto"><div className="dashboard innerpage smart-scroll scrolled-down">              
                                    <a  className='btn btn-green height-40 d-inline-flex align-items-center font-14 text-decoration-none px-4 me-2'
                                        onClick={() =>
                                            navigate(
                                            `/upload-tracker-data/${state?.data?.schoolCount[0]?.schoolId}`
                                            )
                                        }>
                                        <span>📤 Upload Data</span>
                                    </a>  
                                     <a
                                        className="btn btn-gold-f1ac18 height-40 d-inline-flex align-items-center font-14 text-decoration-none px-4 me-2"
                                        onClick={() =>
                                            handleOpenModal()
                                        }>
                                        <span>View Logs</span>
                                    </a>
                                   <a
                                    className='btn btn-success height-40 d-inline-flex align-items-center font-14 text-decoration-none px-4 me-2'
                                    onClick={() =>
                                        ExportToExcelCommon(
                                            allRows.map((row:any) => ({
                                                "Field Name": row.fieldLabel,
                                                "Data Received": row.rawExcelCount,
                                                "Current Fields": row.rawFrontendCount,
                                                "Increase": Number(row.rawFrontendCount) > Number(row.rawExcelCount) ? Number(row.rawFrontendCount) - Number(row.rawExcelCount) : 0,
                                                // "Updated Excel": row.updatedExcelCount,
                                                // "Updated Frontend": row.updatedFrontendCount,
                                                "% Growth": row.completionPercentage,
                                                // "Last Update": getFormattedDate(row.lastUpdatedAt)
                                            })),
                                            `School_Statistics_${state?.data?.schoolCount?.[0]?.schoolId}`
                                        )
                                    }>
                                    <span className="d-flex align-items-center font-500">
                                        📊 Export Excel
                                    </span> </a>

                                    <a
                                    className='btn btn-info height-40 d-inline-flex align-items-center font-14 text-decoration-none px-4 me-2'
                                    onClick={() =>
                                        downloadExportToPPT(state?.data?.schoolCount?.[0]?.schoolId)
                                    }>
                                    <span className="d-flex align-items-center font-500">
                                        📊 Export Slide
                                    </span> </a>
                                </div>
                            </div>
                        </div> 
                    </div>
                </div>
                
                <table className="table">
                    <thead>
                        <tr>
                            <th>Sr. No.</th>
                            <th style={{width:250}}>Field Name</th>
                            <th className="text-center">📊 Data Received</th>
                            <th className="text-center">👥 Current Fields</th>
                            <th className="text-center">Increase</th>
                            <th className="text-center">% Growth</th>
                        </tr>
                    </thead>
                <tbody>
                    {Object.entries(groupedRows).map(([category, rows]) => {
                        const isExpanded = !!expandedCategories[category];
                        return (
                            <React.Fragment key={category}>
                                <tr className={`${isExpanded ? 'table-active' : 'table-bordered'}`} aria-colspan={1} onClick={() => toggleCategory(category)} style={{ cursor: 'pointer' }}>
                                    {/* <td colSpan={6} className="font-600">{isExpanded ? '➖ ' : '➕ '}{rows[0]['type']} - {category}</td> */}
                                    <td colSpan={6} className="font-600">
                                        {isExpanded ? '➖ ' : '➕ '}
                                        <span className="smart-tooltip" data-bs-toggle="tooltip" data-bs-title={`${rows[0]['type']}`} >
                                            <i className={`bi bi-${rows[0]['type'] == "Profile Field" ? "person" : "star"}`}></i> {category}
                                        </span>
                                    </td>
                                </tr>
                                {isExpanded && rows.map((row:any) => {
                                    return (
                                        <tr key={row.trackerFieldMasterId}>
                                            <td>{rowIndexMap[row.trackerFieldMasterId]}</td>
                                            <td>{row.fieldLabel}</td>
                                            <td className="text-center">{row.rawExcelCount}</td>
                                            <td className="text-center">{row.rawFrontendCount}</td>
                                            <td className="text-center">{(Number(row.rawFrontendCount) > Number(row.rawExcelCount)) ? (Number(row.rawFrontendCount) - Number(row.rawExcelCount)) : 0}</td>
                                            <td className="text-center">{row.completionPercentage}</td>
                                        </tr>
                                    )
                                })}
                            </React.Fragment>
                        )
                    })}
                </tbody>
            </table>
            </div>
            </div>
        </>
    );
};

export default SchoolStatisticGrid;
